import {
  type CompleteMfaEnrollmentDto,
  type CreatePatDto,
  type MfaEnrollmentCompletedResponseDto,
  type MfaEnrollmentResponseDto,
  type PatResponseDto,
  type PatWithSecretResponseDto,
  type StaffLoginDto,
  type StaffSessionTokensResponseDto,
  type StartMfaEnrollmentDto,
  v2AuthControllerCompleteMfaEnrollment,
  v2AuthControllerLogin,
  v2AuthControllerStartMfaEnrollment,
  v2PatControllerCreate,
  v2PatControllerList,
  v2PatControllerRevoke,
  v2PatControllerRotate
} from '@idc-forms/api-client'
import { getErrorMessage } from '@/shared/utils/handle-api-error'

type ProblemDetails = {
  status?: number
  code?: string
  detail?: string
  requestId?: string
}

const fallbackApiV2BaseUrl = import.meta.env.VITE_API_URL_NEST.replace(/\/api\/v1\/?$/, '')
const API_V2_BASE_URL = (import.meta.env.VITE_API_URL_V2 || fallbackApiV2BaseUrl).replace(/\/$/, '')

const requestOptions = {
  baseUrl: API_V2_BASE_URL,
  credentials: 'include' as const,
  throwOnError: true as const
}

function bearer(accessToken: string) {
  return { Authorization: `Bearer ${accessToken}` }
}

export function isProblemDetails(error: unknown): error is ProblemDetails {
  if (typeof error !== 'object' || error === null) return false
  const candidate = error as ProblemDetails
  return (
    typeof candidate.status === 'number' ||
    typeof candidate.code === 'string' ||
    typeof candidate.detail === 'string'
  )
}

export function getProblemStatus(error: unknown): number | undefined {
  return isProblemDetails(error) && typeof error.status === 'number' ? error.status : undefined
}

export function getProblemCode(error: unknown): string | undefined {
  return isProblemDetails(error) && typeof error.code === 'string' ? error.code : undefined
}

export function getProblemMessage(error: unknown): string {
  return getErrorMessage(error)
}

export const apiTokenService = {
  stepUp: async (body: StaffLoginDto): Promise<StaffSessionTokensResponseDto> => {
    const result = await v2AuthControllerLogin({ ...requestOptions, body })
    return result.data
  },

  startMfaEnrollment: async (
    accessToken: string,
    body: StartMfaEnrollmentDto
  ): Promise<MfaEnrollmentResponseDto> => {
    const result = await v2AuthControllerStartMfaEnrollment({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  completeMfaEnrollment: async (
    accessToken: string,
    body: CompleteMfaEnrollmentDto
  ): Promise<MfaEnrollmentCompletedResponseDto> => {
    const result = await v2AuthControllerCompleteMfaEnrollment({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  list: async (accessToken: string): Promise<PatResponseDto[]> => {
    const result = await v2PatControllerList({
      ...requestOptions,
      headers: bearer(accessToken)
    })
    return result.data
  },

  create: async (accessToken: string, body: CreatePatDto): Promise<PatWithSecretResponseDto> => {
    const result = await v2PatControllerCreate({
      ...requestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  rotate: async (accessToken: string, id: string): Promise<PatWithSecretResponseDto> => {
    const result = await v2PatControllerRotate({
      ...requestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return result.data
  },

  revoke: async (accessToken: string, id: string): Promise<void> => {
    await v2PatControllerRevoke({
      ...requestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  }
}
